import React, { useEffect, useMemo } from 'react'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import PropTypes from 'prop-types'

import Loader from '../../Loader/Loader'
import { GRID_LAYOUT } from '../../../actions/ui'
import IdealMaxChart from '../SeasonEvolution/IdealMaxChart'
import IdealCurveChart from '../SeasonEvolution/IdealCurveChart'
import EstimationChart from '../SeasonEvolution/EstimationChart'
import SwitchLayoutIcon from '../../../containers/DashboardContainers/SwitchLayoutIconContainer'
import { ChartTitle, FullSizeContainer, FullWidthColumn, Row, TabTitle, Column } from '../Layout'

import styled from 'styled-components'

const HeaderContainer = styled(Row)`
  justify-content: space-between;
`

// predTech charts

/**
 * Renders the PredTech component.
 * @param {Object} props - The component props.
 * @param {Function} props.t - The translation function.
 * @param {Function} props.fetchPredTechByLogType - The function to fetch PredTech data by log type.
 * @param {Function} props.fetchPredTechLayersLog - The function to fetch PredTech layers log data.
 * @param {Array} props.selectedFieldsIds - The selected field IDs.
 * @param {boolean} props.isLoading - Indicates if the component is loading.
 * @param {Object} props.sacHa - The sacHa data.
 * @param {Object} props.prodHa - The prodHa data.
 * @param {Object} props.nitro - The nitro data.
 * @param {Object} props.tchPred - The tchPred data.
 * @param {Object} props.polPred - The polPred data.
 * @param {string} props.chartsLayout - The layout of the charts.
 * @returns {JSX.Element} The rendered PredTech component.
 */
export default function PredTech(props) {
  const {
    t,
    fetchPredTechByLogType,
    fetchPredTechLayersLog,
    selectedFieldsIds,
    isLoading,
    sacHa,
    prodHa,
    nitro,
    tchPred,
    polPred,
    prodphEstimation,
    atrEstimation,
    polEstimation,
    sacEstimation,
    nBunchesEstimation,
    nitrogenEstimation,
    chartsLayout
  } = props

  const areFieldsSelected = useMemo(() => {
    return get(selectedFieldsIds, 'length') > 0
  }, [selectedFieldsIds])

  const hasEstimationData =
    (prodphEstimation && !isEmpty(prodphEstimation.normalized)) ||
    (atrEstimation && !isEmpty(atrEstimation.normalized)) ||
    (polEstimation && !isEmpty(polEstimation.normalized)) ||
    (sacEstimation && !isEmpty(sacEstimation.normalized)) ||
    (nBunchesEstimation && !isEmpty(nBunchesEstimation.normalized)) ||
    (nitrogenEstimation && !isEmpty(nitrogenEstimation.normalized))

  const hasHarvestLogisticsData =
    (sacHa && !isEmpty(sacHa.normalized)) ||
    (prodHa && !isEmpty(prodHa.normalized)) ||
    (nitro && !isEmpty(nitro.normalized)) ||
    (tchPred && !isEmpty(tchPred.normalized)) ||
    (polPred && !isEmpty(polPred.normalized))

  const isGridLayout = chartsLayout === GRID_LAYOUT
  const chartsWidth = isGridLayout ? '50%' : '100%'

  useEffect(() => {
    if (selectedFieldsIds.length > 0) {
      fetchPredTechByLogType({ fieldIds: selectedFieldsIds, mode: 'weekly' })
      fetchPredTechByLogType({ fieldIds: selectedFieldsIds, mode: 'ideal_season' })
      fetchPredTechByLogType({ fieldIds: selectedFieldsIds, mode: 'season_max' })
      fetchPredTechLayersLog({ fieldIds: selectedFieldsIds })
    }
  }, [fetchPredTechByLogType, fetchPredTechLayersLog, selectedFieldsIds])

  /**
   * Renders the SacHa charts.
   * @returns {JSX.Element} The rendered SacHa charts.
   */
  const SacHaCharts = () => (
    <Row>
      <IdealCurveChart t={t} title="estimación sacarosa" data={sacHa} areFieldsSelected={areFieldsSelected} chartsWidth={chartsWidth} yLabel={t('Suc/Ha')} />
      <IdealMaxChart t={t} title="estimación sacarosa" data={sacHa} areFieldsSelected={areFieldsSelected} chartsWidth={chartsWidth} yLabel={t('Suc/Ha')} />
    </Row>
  )

  /**
   * Renders the ProdHa charts.
   * @returns {JSX.Element} The rendered ProdHa charts.
   */
  const ProdHaCharts = () => (
    <Row>
      <IdealCurveChart
        t={t}
        title="Estimacion produccion ( Tn / Ha )"
        data={prodHa}
        areFieldsSelected={areFieldsSelected}
        chartsWidth={chartsWidth}
        yLabel={'Tn/Ha'}
      />
      <IdealMaxChart
        t={t}
        title="Estimacion produccion ( Tn / Ha )"
        data={prodHa}
        areFieldsSelected={areFieldsSelected}
        chartsWidth={chartsWidth}
        yLabel={'Tn/Ha'}
      />
    </Row>
  )

  /**
   * Renders the Nitro charts.
   * @returns {JSX.Element} The rendered Nitro charts.
   */
  const NitroCharts = () => (
    <Row>
      <IdealCurveChart t={t} title="Nitrogeno %" data={nitro} areFieldsSelected={areFieldsSelected} chartsWidth={chartsWidth} yLabel={'%'} />
      <IdealMaxChart t={t} title="Nitrogeno %" data={nitro} areFieldsSelected={areFieldsSelected} chartsWidth={chartsWidth} yLabel={'%'} />
    </Row>
  )

  /**
   * Renders the Tch charts.
   * @returns {JSX.Element} The rendered Tch charts.
   */
  const TchCharts = () => (
    <Row>
      <IdealCurveChart t={t} title="TCH  ( Tn / Ha )" data={tchPred} areFieldsSelected={areFieldsSelected} chartsWidth={chartsWidth} yLabel={'Tn/Ha'} />
      <IdealMaxChart t={t} title="TCH  ( Tn / Ha )" data={tchPred} areFieldsSelected={areFieldsSelected} chartsWidth={chartsWidth} yLabel={'Tn/Ha'} />
    </Row>
  )

  /**
   * Renders the Pol charts.
   * @returns {JSX.Element} The rendered Pol charts.
   */
  const PolCharts = () => (
    <Row>
      <IdealCurveChart t={t} title="POL ( ºBx )" data={polPred} areFieldsSelected={areFieldsSelected} chartsWidth={chartsWidth} yLabel={'ºBx'} />
      <IdealMaxChart t={t} title="POL ( ºBx )" data={polPred} areFieldsSelected={areFieldsSelected} chartsWidth={chartsWidth} yLabel={'ºBx'} />
    </Row>
  )

  const ProdphEstimationChart = () => (
    <EstimationChart t={t} title={'Production/ha Estimation ( Tn / Ha )'} data={prodphEstimation} chartsWidth={chartsWidth} yLabel={'Tn/Ha'} />
  )

  const ATREstimationChart = () => <EstimationChart t={t} title={'ATR Estimation'} data={atrEstimation} chartsWidth={chartsWidth} yLabel={'Kg/Tn'} />

  const POLEstimationChart = () => <EstimationChart t={t} title={'POL Estimation'} data={polEstimation} chartsWidth={chartsWidth} yLabel={'ºBx'} />

  const SACEstimationChart = () => <EstimationChart t={t} title={'SAC Estimation'} data={sacEstimation} chartsWidth={chartsWidth} yLabel={'Tn/Ha'} />

  const NBunchesEstimation = () => (
    <EstimationChart t={t} title={'N Bunches Estimation'} data={nBunchesEstimation} chartsWidth={chartsWidth} yLabel={'Bunches/Ha'} />
  )

  const NitrogenEstimation = () => <EstimationChart t={t} title={'Nitrogen Estimation'} data={nitrogenEstimation} chartsWidth={chartsWidth} yLabel={'%'} />

  return (
    <FullSizeContainer>
      <Loader zIndex={1} isLoading={isLoading} />
      <Row>
        <FullWidthColumn>
          <HeaderContainer noMarginBottom>
            <Column width="auto">
              <TabTitle>
                {'PredTech ' + t('SEASON_EVOLUTION')} {areFieldsSelected}
              </TabTitle>
            </Column>
            {areFieldsSelected ? (
              <Column width="auto">
                <SwitchLayoutIcon />
              </Column>
            ) : null}
          </HeaderContainer>
          {areFieldsSelected ? (
            <>
              {hasHarvestLogisticsData && (
                <HeaderContainer noMarginBottom>
                  <Column width="auto">
                    <TabTitle>{t('HARVEST_LOGISTICS')}</TabTitle>
                  </Column>
                </HeaderContainer>
              )}

              {sacHa && !isEmpty(sacHa.normalized) && <SacHaCharts />}
              {prodHa && !isEmpty(prodHa.normalized) && <ProdHaCharts />}
              {nitro && !isEmpty(nitro.normalized) && <NitroCharts />}
              {tchPred && !isEmpty(tchPred.normalized) && <TchCharts />}
              {polPred && !isEmpty(polPred.normalized) && <PolCharts />}

              {hasEstimationData && (
                <HeaderContainer noMarginBottom>
                  <Column width="auto">
                    <TabTitle>{t('FORECAST_ANALYTICS')}</TabTitle>
                  </Column>
                </HeaderContainer>
              )}

              <Row>
                {prodphEstimation && !isEmpty(prodphEstimation.normalized) && <ProdphEstimationChart />}
                {atrEstimation && !isEmpty(atrEstimation.normalized) && <ATREstimationChart />}
                {polEstimation && !isEmpty(polEstimation.normalized) && <POLEstimationChart />}
                {sacEstimation && !isEmpty(sacEstimation.normalized) && <SACEstimationChart />}
                {nBunchesEstimation && !isEmpty(nBunchesEstimation.normalized) && <NBunchesEstimation />}
                {nitrogenEstimation && !isEmpty(nitrogenEstimation.normalized) && <NitrogenEstimation />}
              </Row>
            </>
          ) : (
            <ChartTitle>{t('select at least one field')}</ChartTitle>
          )}
        </FullWidthColumn>
      </Row>
    </FullSizeContainer>
  )
}

PredTech.propTypes = {
  sacHa: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  polPred: PropTypes.object.isRequired,
  chartsLayout: PropTypes.string.isRequired,
  selectedFieldsIds: PropTypes.array.isRequired,
  fetchPredTechByLogType: PropTypes.func.isRequired,
  fetchPredTechLayersLog: PropTypes.func.isRequired
}
